import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import Hold from './Hold';
import {
    SaleItemPrices,
    NetItemPrices,
    RetailItemPrices,
    PrivateOfferItemPrices,
} from './PdpSharedItemPrices';
import { textPriceValue } from '../messages/pdpPriceMessages';

// styles
import styles from './PdpSharedSidebarPrice.scss';

// helpers
import get from 'lodash.get';
import { getPriceMessagesTypes } from '../messages/pdpPriceMessages';

function PdpSharedSidebarPrice({ currency, item, displayInfo }) {
    const holdDisplayTime = get(item, 'activeHold.displayTimeToExpire') || null;
    const pricing = get(item, 'pricing');
    const pricingDisplayInfo = get(displayInfo, 'pricing');
    const transaction = get(item, 'lastTransaction');
    const privateOffer = get(transaction, 'isPrivateOffer') && get(transaction, 'offers[0]');
    const canUsePrivateOfferForCheckout = get(privateOffer, 'canUseOfferForCheckout');

    if (!pricing || !pricingDisplayInfo) {
        return <div className={styles.wrapper} />;
    }

    // hold types include - ON_HOLD & ON_HOLD_CUSTOM
    // text price types include - sold, unavailable & pur
    const { holdType, textPriceType } = getPriceMessagesTypes(pricingDisplayInfo);
    const {
        showOriginal,
        showRetail,
        showSale,
        showNet,
        showListPriceLabel,
        quantityDisplay,
    } = pricingDisplayInfo;

    // Getting priceComponent
    let priceComponent = null;
    const priceComponentProps = {
        pricing,
        currency,
        showListPriceLabel,
        quantityDisplay,
    };
    if (canUsePrivateOfferForCheckout) {
        const privateOfferAmount = get(privateOffer, 'totalAmount.convertedAmountList').find(
            amount => {
                return amount.currency === currency;
            }
        ).amount;
        priceComponent = (
            <PrivateOfferItemPrices
                {...priceComponentProps}
                amount={privateOfferAmount}
                expiration={privateOffer.expirationDate}
                showExpirationDate={!holdDisplayTime}
                showRetail={showRetail}
                showNet={showNet}
            />
        );
    } else if (showSale) {
        // Item is on sale
        priceComponent = <SaleItemPrices {...priceComponentProps} showOriginal={showOriginal} />;
    } else if (showNet) {
        // Item has a net price and is being viewed by a trade user
        priceComponent = <NetItemPrices {...priceComponentProps} showRetail={showRetail} />;
    } else if (showRetail) {
        // Item only has a retail price (or is being viewed by a non-trade user)
        priceComponent = <RetailItemPrices {...priceComponentProps} />;
    } else if (textPriceType) {
        // Item is unavailable or has no visible price (e.g. sold, unavailable or pur)
        priceComponent = (
            <div className={styles[textPriceType]} data-tn={`pdp-${textPriceType}-price`}>
                {textPriceValue(textPriceType)}
            </div>
        );
    }

    return (
        <div className={styles.wrapper} data-tn="pdp-price-wrapper">
            {/* 'On Hold For You' OR 'On Hold' */}
            <Hold holdType={holdType} holdDisplayTime={holdDisplayTime} />
            {/* Price */}
            {priceComponent}
        </div>
    );
}

PdpSharedSidebarPrice.propTypes = {
    currency: PropTypes.string,
    item: PropTypes.object.isRequired,
    displayInfo: PropTypes.object,
};

export default createFragmentContainer(PdpSharedSidebarPrice, {
    item: graphql`
        fragment PdpSharedSidebarPrice_item on Item {
            activeHold {
                displayTimeToExpire
            }
            pricing {
                originalPrice {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                retailPrice {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                salePrice {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                netPrice {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
            }
            lastTransaction(buyerId: $userId) @include(if: $fetchUser) {
                isPrivateOffer
                offers {
                    canUseOfferForCheckout
                    expirationDate
                    totalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                }
            }
        }
    `,
    displayInfo: graphql`
        fragment PdpSharedSidebarPrice_displayInfo on ItemDisplayInfo {
            pricing {
                quantityDisplay
                showSold
                showUnavailable
                showPUR
                showNet
                showSale
                showRetail
                showOriginal
                showOnHold
                showOnHoldCustom
                showListPriceLabel
            }
        }
    `,
});
